<script setup lang="ts">
import { ref } from 'vue'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { faker } from '@faker-js/faker/locale/en'
import { useFormValidation } from '@/composables/useFormValidation'
import {
  validateRequired,
  validatePositiveAmount,
  validateMonthRange,
} from '@/lib/validation'

interface Insurance {
  nameOfPersonCoveredByPolicy: string
  amountOfGrossPremium: string
  amountOfMonthsPolicyWasActive: string
  isChild: boolean
  isForOfficeUseOnly: boolean
}

interface Props {
  open: boolean
  onSubmit: (data: Insurance) => void
  onOpenChange: (open: boolean) => void
}

const props = defineProps<Props>()

const formState = {
  nameOfPersonCoveredByPolicy: ref(''),
  amountOfGrossPremium: ref(''),
  amountOfMonthsPolicyWasActive: ref(''),
  isChild: ref(false),
  isForOfficeUseOnly: ref(false),
}

const validationSchema = {
  nameOfPersonCoveredByPolicy: validateRequired,
  amountOfGrossPremium: validatePositiveAmount,
  amountOfMonthsPolicyWasActive: validateMonthRange,
}

const {
  errors,
  isValid,
  handleFieldTouch,
  markAllFieldsTouched,
  getInputClass,
} = useFormValidation(validationSchema, formState)

const handleSubmit = () => {
  markAllFieldsTouched()
  if (!isValid.value) return

  props.onSubmit({
    nameOfPersonCoveredByPolicy: formState.nameOfPersonCoveredByPolicy.value,
    amountOfGrossPremium: formState.amountOfGrossPremium.value,
    amountOfMonthsPolicyWasActive:
      formState.amountOfMonthsPolicyWasActive.value,
    isChild: formState.isChild.value,
    isForOfficeUseOnly: formState.isForOfficeUseOnly.value,
  })

  // Reset form
  formState.nameOfPersonCoveredByPolicy.value = ''
  formState.amountOfGrossPremium.value = ''
  formState.amountOfMonthsPolicyWasActive.value = ''
  formState.isChild.value = false
  formState.isForOfficeUseOnly.value = false
}

const fillMockData = () => {
  formState.nameOfPersonCoveredByPolicy.value = faker.person.fullName()
  formState.amountOfGrossPremium.value = faker.number
    .int({ min: 100, max: 5000 })
    .toString()
  formState.amountOfMonthsPolicyWasActive.value = faker.number
    .int({ min: 1, max: 12 })
    .toString()
  formState.isChild.value = faker.datatype.boolean()
  formState.isForOfficeUseOnly.value = faker.datatype.boolean()
}
</script>

<template>
  <Dialog :open="open" @update:open="onOpenChange">
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Add Medical Insurance</DialogTitle>
      </DialogHeader>

      <form @submit.prevent="handleSubmit" class="space-y-6">
        <div class="space-y-2">
          <Label for="name">Name of person covered by policy *</Label>
          <Input
            id="name"
            v-model="formState.nameOfPersonCoveredByPolicy.value"
            :class="getInputClass('nameOfPersonCoveredByPolicy')"
            @blur="handleFieldTouch('nameOfPersonCoveredByPolicy')"
          />
          <p
            v-if="errors.nameOfPersonCoveredByPolicy"
            class="text-sm text-red-500"
          >
            {{ errors.nameOfPersonCoveredByPolicy }}
          </p>
        </div>

        <div class="space-y-2">
          <Label for="premium">Amount of gross premium *</Label>
          <Input
            id="premium"
            type="number"
            v-model="formState.amountOfGrossPremium.value"
            placeholder="€ 0.00"
            :class="getInputClass('amountOfGrossPremium')"
            @blur="handleFieldTouch('amountOfGrossPremium')"
          />
          <p v-if="errors.amountOfGrossPremium" class="text-sm text-red-500">
            {{ errors.amountOfGrossPremium }}
          </p>
        </div>

        <div class="space-y-2">
          <Label for="months">Amount of months policy was active *</Label>
          <Input
            id="months"
            type="number"
            v-model="formState.amountOfMonthsPolicyWasActive.value"
            placeholder="Enter number of months (1-12)"
            :class="getInputClass('amountOfMonthsPolicyWasActive')"
            @blur="handleFieldTouch('amountOfMonthsPolicyWasActive')"
          />
          <p
            v-if="errors.amountOfMonthsPolicyWasActive"
            class="text-sm text-red-500"
          >
            {{ errors.amountOfMonthsPolicyWasActive }}
          </p>
        </div>

        <div class="flex items-center space-x-2">
          <Switch id="child" v-model="formState.isChild.value" />
          <Label for="child">Child policy</Label>
        </div>

        <div class="flex items-center space-x-2">
          <Switch id="office" v-model="formState.isForOfficeUseOnly.value" />
          <Label for="office">For office use only</Label>
        </div>

        <div class="flex gap-4">
          <Button type="submit" class="flex-1">Add insurance</Button>
          <Button
            type="button"
            variant="secondary"
            class="flex-1"
            @click="() => onOpenChange(false)"
          >
            Cancel
          </Button>
        </div>

        <!-- Mock Data Button (Development Only) -->
        <Button
          type="button"
          variant="outline"
          class="w-full"
          @click="fillMockData"
        >
          Fill Mock Data
        </Button>
      </form>
    </DialogContent>
  </Dialog>
</template>
